import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Loader from '../../components/common/Loader';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import hostService from '../../api/services/hostService';
import contestService from '../../api/services/contestService';
import { handleApiError } from '../../utils/helpers';

const DashboardPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    hosts: 0,
    contests: 0,
    activeContests: 0,
    totalVotes: 0
  });

  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  useEffect(() => {
    fetchDashboardData();
  }, []);

const fetchDashboardData = async () => {
  try {
    setLoading(true);
    
    // Fetch hosts - Super Admin gets all, Company Admin gets their own
    const hostsResponse = isSuperAdmin 
      ? await hostService.getAllHosts({ pageSize: 100 })
      : await hostService.getMyHosts({ pageSize: 100 });

    // console.log('Hosts response:', hostsResponse);
    
    // Fetch contests
    const contestsResponse = await contestService.getAllContests({ pageSize: 100 });
    
    const contests = contestsResponse.data?.data || contestsResponse.data || [];
    const activeContests = contests.filter(c => c.status === 'ACTIVE').length;
    const totalVotes = contests.reduce((sum, c) => sum + (c._count?.votes || 0), 0);

    setStats({
      // ✅ FIXED: Access pagination.total directly
      hosts: hostsResponse.pagination?.total || 0,
      contests: contests.length,
      activeContests,
      totalVotes
    });
  } catch (err) {
    console.error('Error fetching dashboard data:', handleApiError(err));
  } finally {
    setLoading(false);
  }
};
  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <Loader size="lg" />
      </DashboardLayout>
    );
  }

  // console.log('Dashboard stats:', stats);

  return (
    <DashboardLayout
      title="Dashboard"
      subtitle={`Welcome back, ${user?.firstName}!`}
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Total Hosts"
          value={stats.hosts}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          color="bg-blue-500"
          linkTo="/admin/hosts"
        />

        <StatCard
          title="Total Contests"
          value={stats.contests}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
          color="bg-green-500"
          linkTo="/admin/contests"
        />

        <StatCard
          title="Active Contests"
          value={stats.activeContests}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
          color="bg-yellow-500"
          linkTo="/admin/contests"
        />

        {/* <StatCard
          title="Total Votes"
          value={stats.totalVotes}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          }
          color="bg-purple-500"
          linkTo="/admin/votes"
        /> */}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Quick Actions">
          <div className="space-y-3">
            <Link
              to="/admin/hosts"
              className="block p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Manage Hosts</h4>
                  <p className="text-sm text-gray-600">Add or edit your organizations</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>

            <Link
              to="/admin/contests"
              className="block p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Manage Contests</h4>
                  <p className="text-sm text-gray-600">Create and manage your contests</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>

            <Link
              to="/admin/statistics"
              className="block p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">View Statistics</h4>
                  <p className="text-sm text-gray-600">Analyze your contest performance</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          </div>
        </Card>

        <Card title="Getting Started">
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-semibold">1</span>
                </div>
              </div>
              <div className="ml-4">
                <h4 className="font-medium text-gray-900">Create a Host</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Set up your organization profile
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-semibold">2</span>
                </div>
              </div>
              <div className="ml-4">
                <h4 className="font-medium text-gray-900">Create a Contest</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Add categories and contestants
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-semibold">3</span>
                </div>
              </div>
              <div className="ml-4">
                <h4 className="font-medium text-gray-900">Activate & Share</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Make your contest live and start receiving votes
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, linkTo }) => {
  return (
    <Link to={linkTo}>
      <Card hover className="h-full">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
          </div>
          <div className={`${color} text-white p-3 rounded-lg`}>
            {icon}
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default DashboardPage;